<template>
  <div
    class="pump-test-stand"
    :class="{ 'animations-paused': animationsPaused }"
  >
    <h6 class="fw-semibold mb-3">Pump Test Stand</h6>

    <!-- Overheat Warning -->
    <div
      v-if="pumpOverheat"
      class="alert alert-danger d-flex align-items-center py-2 mb-3"
      role="alert"
    >
      <i
        class="bi bi-exclamation-triangle-fill me-2 fs-5"
        aria-hidden="true"
      ></i>
      <div>
        <strong>Don't break the pump!</strong>
        The valve is nearly closed (&lt;&nbsp;5% open). The pump is overheating
        &mdash; open the valve or turn off the pump.
      </div>
    </div>

    <!-- Test Stand Schematic -->
    <div class="card mb-3 border-secondary">
      <div class="card-body py-3 bg-light">
        <!-- Instrument readout row -->
        <div
          class="d-flex align-items-center justify-content-center gap-2 flex-wrap mb-3"
        >
          <div
            class="gauge-box border rounded p-2 bg-white text-center"
            style="min-width: 130px"
          >
            <div class="small text-muted mb-1">P<sub>suction</sub></div>
            <div aria-live="polite" aria-atomic="true">
              <template v-if="settling">
                <span class="text-warning fw-semibold">
                  <span
                    class="spinner-border spinner-border-sm me-1"
                    aria-hidden="true"
                  ></span
                  >Settling&hellip;
                </span>
              </template>
              <template v-else-if="readings">
                <span class="fw-bold"
                  >{{ readings.suctionPressure_psi.toFixed(2) }}&nbsp;psi</span
                >
              </template>
              <span v-else class="text-muted">&mdash;</span>
            </div>
          </div>

          <div
            class="gauge-box border rounded p-2 bg-white text-center"
            style="min-width: 130px"
          >
            <div class="small text-muted mb-1">P<sub>discharge</sub></div>
            <div aria-live="polite" aria-atomic="true">
              <template v-if="settling">
                <span class="text-warning fw-semibold">
                  <span
                    class="spinner-border spinner-border-sm me-1"
                    aria-hidden="true"
                  ></span
                  >Settling&hellip;
                </span>
              </template>
              <template v-else-if="readings">
                <span class="fw-bold"
                  >{{
                    readings.dischargePressure_psi.toFixed(2)
                  }}&nbsp;psi</span
                >
              </template>
              <span v-else class="text-muted">&mdash;</span>
            </div>
          </div>

          <div
            class="gauge-box border rounded p-2 bg-white text-center"
            style="min-width: 130px"
          >
            <div class="small text-muted mb-1">Flow Meter</div>
            <div aria-live="polite" aria-atomic="true">
              <template v-if="settling">
                <span class="text-warning fw-semibold">
                  <span
                    class="spinner-border spinner-border-sm me-1"
                    aria-hidden="true"
                  ></span
                  >Settling&hellip;
                </span>
              </template>
              <template v-else-if="readings">
                <div class="fw-bold">
                  {{ readings.flowRate_GPM.toFixed(1) }}&nbsp;GPM
                </div>
              </template>
              <span v-else class="text-muted">&mdash;</span>
            </div>
          </div>
        </div>

        <!-- Piping SVG schematic -->
        <PumpTestStandSchematic
          ref="schematic"
          :animations-paused="animationsPaused"
          :pump-on="pumpOn"
          :valve-position="valvePosition"
          :pump-overheat="pumpOverheat"
          @toggle-pump="togglePump"
          @handwheel-pointerdown="startDrag"
          @handwheel-keydown="onHandwheelKey"
          @handwheel-focus="onHandwheelFocus"
          @handwheel-blur="onHandwheelBlur"
        />
      </div>
    </div>

    <!-- Summary bar -->
    <div
      v-if="readings && !settling"
      class="alert alert-info py-2 small mb-0"
      aria-live="polite"
      role="status"
    >
      <strong>Current Readings:</strong>&ensp; P<sub>s</sub>&nbsp;=&nbsp;{{
        readings.suctionPressure_psi.toFixed(2)
      }}&nbsp;psi &ensp;|&ensp; P<sub>d</sub>&nbsp;=&nbsp;{{
        readings.dischargePressure_psi.toFixed(2)
      }}&nbsp;psi &ensp;|&ensp; Q&nbsp;=&nbsp;{{
        readings.flowRate_GPM.toFixed(1)
      }}&nbsp;GPM
    </div>
  </div>
</template>

<script>
import { getTestStandReadings } from "@/utils/preLab.js";
import { pumpTestStand } from "@/data/pumpTestStand.js";
import PumpTestStandSchematic from "@/components/activity/PumpTestStandSchematic.vue";
import { gsap } from "gsap";

const GATE_CLOSED_Y = 82;
const GATE_TRAVEL = 10;
const NEEDLE_MIN_DEG = -60;
const NEEDLE_MAX_DEG = 60;
const TURNS_FOR_FULL_OPEN = 2;
const MIN_VALVE_PCT_FOR_FLOW = 5;
const FT_NEEDLE_MIN_DEG = -80;
const FT_NEEDLE_SWEEP_DEG = 160;
const PERCENT_SCALE = 100;
const DEG_PER_TURN = 360;
const FLOW_DASH_PERIOD = 28; // stroke-dasharray 16 + gap 12
const FLOW_TWEEN_DURATION_S = 0.7;
const IMPELLER_ROTATION_DEG = 360;
const IMPELLER_TWEEN_DURATION_S = 1.5;
const MIN_IMPELLER_SCALE = 0.3;
const KEYBOARD_STEP_PCT = 5;
const KEYBOARD_BIG_STEP_PCT = 20;
const GAUGE_ANIMATION_DURATION_S = 0.4;
const HANDWHEEL_KEYBOARD_TWEEN_S = 0.3;
const HANDWHEEL_SLIDER_TWEEN_S = 0.4;
const HANDWHEEL_CENTER_X = 650;
const HANDWHEEL_CENTER_Y = -34;
const HANDWHEEL_ORIGIN = `${HANDWHEEL_CENTER_X} ${HANDWHEEL_CENTER_Y}`;
const PRESSURE_GAUGE_ORIGIN = "512 100";
const FLOW_GAUGE_ORIGIN = "810 100";
const SLIDER_STEP_PCT = 5;
const SOUND_BASE_URL = import.meta.env.BASE_URL || "/";
const WHEEL_SOUND_PATH = `${SOUND_BASE_URL}sounds/valve-turn.wav`;
const WHEEL_SOUND_THROTTLE_MS = 90;
const WHEEL_KEY_SOUND_DURATION_MS = 140;
const WHEEL_SOUND_VOLUME = 0.45;
const WHEEL_SOUND_START_S = 0.04;
const PUMP_START_SOUND_PATH = `${SOUND_BASE_URL}sounds/pump-start.wav`;
const PUMP_STOP_SOUND_PATH = `${SOUND_BASE_URL}sounds/pump-stop.wav`;
const OVERHEAT_ALERT_SOUND_PATH = `${SOUND_BASE_URL}sounds/overheat-alert.wav`;
const STATUS_SOUNDS_ENABLED =
  import.meta.env.VITE_ENABLE_PUMP_STATUS_SOUNDS === "true";
const PUMP_TOGGLE_SOUND_VOLUME = 0.4;
const OVERHEAT_ALERT_VOLUME = 0.35;

export default {
  name: "PumpTestStand",
  components: {
    PumpTestStandSchematic
  },
  emits: ["readings-ready"],
  props: {
    animationsPaused: {
      type: Boolean,
      default: false
    },
    soundsMuted: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      pumpOn: false,
      valvePosition: 0,
      readings: null,
      settling: false,
      settleTimer: null,
      dragActive: false,
      dragLastAngle: 0,
      wheelRotation: 0,
      handwheelFocused: false,
      wheelAudio: null,
      lastWheelSoundAtMs: 0,
      wheelKeySoundStopTimer: null,
      pumpStartAudio: null,
      pumpStopAudio: null,
      overheatAlertAudio: null
    };
  },
  computed: {
    pumpOverheat() {
      return this.pumpOn && this.valvePosition < MIN_VALVE_PCT_FOR_FLOW;
    }
  },
  mounted() {
    this.initWheelAudio();
    this.initStatusAudio();
    this.initAnimations();
    this.syncMotionState();
    this.applyAnimation(this.pumpOn ? this.valvePosition : 0);
  },
  watch: {
    valvePosition(pct) {
      // Skip during drag — applyAnimation is called directly with float precision
      if (!this.dragActive) this.applyAnimation(this.pumpOn ? pct : 0);
    },
    animationsPaused() {
      this.syncMotionState();
    },
    soundsMuted(isMuted) {
      if (!isMuted) return;
      this.stopWheelDragSound();
      this.stopOverheatAlertSound();
    },
    pumpOverheat(isOverheating) {
      if (isOverheating) {
        this.startOverheatAlertSound();
      } else {
        this.stopOverheatAlertSound();
      }
    }
  },
  methods: {
    svgRef(name) {
      return this.$refs?.schematic?.$refs?.[name] || null;
    },

    syncMotionState() {
      if (this.animationsPaused) {
        this._flowTweens?.forEach((t) => t.timeScale(0));
        if (this._impellerTween) this._impellerTween.timeScale(0);
        return;
      }

      this.updateFlowSpeed(this.pumpOn ? this.valvePosition : 0);
    },

    handleAudioError(action, error) {
      console.warn(`[PumpTestStand] ${action}`, error);
    },

    initWheelAudio() {
      try {
        this.wheelAudio = new Audio(WHEEL_SOUND_PATH);
        this.wheelAudio.preload = "auto";
        this.wheelAudio.volume = WHEEL_SOUND_VOLUME;
        this.wheelAudio.loop = false;
      } catch (error) {
        this.wheelAudio = null;
        this.handleAudioError("Failed to initialize wheel audio", error);
      }
    },

    startWheelDragSound() {
      if (this.soundsMuted) return;
      if (!this.wheelAudio) return;
      try {
        if (this.wheelKeySoundStopTimer) {
          clearTimeout(this.wheelKeySoundStopTimer);
          this.wheelKeySoundStopTimer = null;
        }
        this.wheelAudio.loop = true;
        if (this.wheelAudio.readyState < 2) {
          this.wheelAudio.load();
        }

        const maxStart =
          Number.isFinite(this.wheelAudio.duration) &&
          this.wheelAudio.duration > 0
            ? Math.max(0, this.wheelAudio.duration - 0.01)
            : WHEEL_SOUND_START_S;
        this.wheelAudio.currentTime = Math.min(WHEEL_SOUND_START_S, maxStart);
        this.wheelAudio
          .play()
          .catch((error) =>
            this.handleAudioError("Wheel drag playback was blocked", error)
          );
      } catch (error) {
        this.handleAudioError("Wheel drag playback failed", error);
      }
    },

    initStatusAudio() {
      if (!STATUS_SOUNDS_ENABLED) {
        this.pumpStartAudio = null;
        this.pumpStopAudio = null;
        this.overheatAlertAudio = null;
        return;
      }

      try {
        this.pumpStartAudio = new Audio(PUMP_START_SOUND_PATH);
        this.pumpStartAudio.preload = "auto";
        this.pumpStartAudio.volume = PUMP_TOGGLE_SOUND_VOLUME;
      } catch (error) {
        this.pumpStartAudio = null;
        this.handleAudioError("Failed to initialize pump-start audio", error);
      }

      try {
        this.pumpStopAudio = new Audio(PUMP_STOP_SOUND_PATH);
        this.pumpStopAudio.preload = "auto";
        this.pumpStopAudio.volume = PUMP_TOGGLE_SOUND_VOLUME;
      } catch (error) {
        this.pumpStopAudio = null;
        this.handleAudioError("Failed to initialize pump-stop audio", error);
      }

      try {
        this.overheatAlertAudio = new Audio(OVERHEAT_ALERT_SOUND_PATH);
        this.overheatAlertAudio.preload = "auto";
        this.overheatAlertAudio.volume = OVERHEAT_ALERT_VOLUME;
        this.overheatAlertAudio.loop = true;
      } catch (error) {
        this.overheatAlertAudio = null;
        this.handleAudioError(
          "Failed to initialize overheat alert audio",
          error
        );
      }
    },

    playOneShot(audioEl) {
      if (this.soundsMuted) return;
      if (!audioEl) return;
      try {
        audioEl.currentTime = 0;
        audioEl
          .play()
          .catch((error) =>
            this.handleAudioError("One-shot playback was blocked", error)
          );
      } catch (error) {
        this.handleAudioError("One-shot playback failed", error);
      }
    },

    startOverheatAlertSound() {
      if (this.soundsMuted) return;
      if (!this.overheatAlertAudio) return;
      if (!this.overheatAlertAudio.paused) return;
      try {
        this.overheatAlertAudio.currentTime = 0;
        this.overheatAlertAudio
          .play()
          .catch((error) =>
            this.handleAudioError("Overheat alert playback was blocked", error)
          );
      } catch (error) {
        this.handleAudioError("Overheat alert playback failed", error);
      }
    },

    stopOverheatAlertSound() {
      if (!this.overheatAlertAudio) return;
      try {
        this.overheatAlertAudio.pause();
        this.overheatAlertAudio.currentTime = 0;
      } catch (error) {
        this.handleAudioError("Overheat alert cleanup failed", error);
      }
    },
    stopWheelDragSound() {
      if (!this.wheelAudio) return;
      try {
        this.wheelAudio.pause();
        this.wheelAudio.currentTime = 0;
      } catch (error) {
        this.handleAudioError("Wheel drag cleanup failed", error);
      }
    },

    playWheelTurnSound(force = false) {
      if (this.soundsMuted) return;
      const now = Date.now();
      if (!force && now - this.lastWheelSoundAtMs < WHEEL_SOUND_THROTTLE_MS) {
        return;
      }
      this.lastWheelSoundAtMs = now;

      if (!this.wheelAudio) return;

      try {
        this.wheelAudio.loop = false;
        this.wheelAudio.currentTime = 0;
        this.wheelAudio
          .play()
          .catch((error) =>
            this.handleAudioError("Wheel turn playback was blocked", error)
          );
        if (this.wheelKeySoundStopTimer) {
          clearTimeout(this.wheelKeySoundStopTimer);
        }
        this.wheelKeySoundStopTimer = setTimeout(() => {
          this.wheelKeySoundStopTimer = null;
          if (!this.dragActive) {
            this.stopWheelDragSound();
          }
        }, WHEEL_KEY_SOUND_DURATION_MS);
      } catch (error) {
        this.handleAudioError("Wheel turn playback failed", error);
      }
    },

    onHandwheelFocus() {
      this.handwheelFocused = true;
      if (!this.dragActive) {
        this.stopWheelDragSound();
      }
    },

    onHandwheelBlur() {
      this.handwheelFocused = false;
      if (!this.dragActive) {
        this.stopWheelDragSound();
      }
    },

    togglePump() {
      this.pumpOn = !this.pumpOn;
      this.onPumpToggle();
    },

    initAnimations() {
      const flowRefs = [
        this.svgRef("flowSuction"),
        this.svgRef("flowDischarge1"),
        this.svgRef("flowDischarge2"),
        this.svgRef("flowDischarge3"),
        this.svgRef("flowReturn")
      ].filter(Boolean);
      this._flowTweens = flowRefs.map((el) =>
        gsap.fromTo(
          el,
          { strokeDashoffset: 0 },
          {
            strokeDashoffset: -FLOW_DASH_PERIOD,
            duration: FLOW_TWEEN_DURATION_S,
            repeat: -1,
            ease: "none"
          }
        )
      );
      const pumpImpeller = this.svgRef("pumpImpeller");
      this._impellerTween = gsap.to(pumpImpeller, {
        rotation: IMPELLER_ROTATION_DEG,
        svgOrigin: "341 160",
        duration: IMPELLER_TWEEN_DURATION_S,
        repeat: -1,
        ease: "none"
      });
      // Start paused at valve-closed state
      this.updateFlowSpeed(0);
    },

    updateFlowSpeed(pct) {
      if (this.animationsPaused || !this.pumpOn) {
        this._flowTweens?.forEach((t) => t.timeScale(0));
        if (this._impellerTween) this._impellerTween.timeScale(0);
        return;
      }
      const scale = pct / PERCENT_SCALE;
      this._flowTweens?.forEach((t) => t.timeScale(scale));
      // Impeller spins at a base speed when pump is on, faster with more flow
      const impellerScale = Math.max(MIN_IMPELLER_SCALE, scale);
      if (this._impellerTween) this._impellerTween.timeScale(impellerScale);
    },

    applyAnimation(pct) {
      const f = pct / PERCENT_SCALE;
      const gateY = GATE_CLOSED_Y - f * GATE_TRAVEL;
      const needleDeg = NEEDLE_MIN_DEG + f * (NEEDLE_MAX_DEG - NEEDLE_MIN_DEG);
      const gateDisc = this.svgRef("gateDisc");
      const gaugeNeedle = this.svgRef("gaugeNeedle");
      const ftNeedle = this.svgRef("ftNeedle");
      const valvePctLabel = this.svgRef("valvePctLabel");

      if (this.animationsPaused) {
        gsap.set(gateDisc, {
          attr: { y: gateY }
        });
        gsap.set(gaugeNeedle, {
          rotation: needleDeg,
          svgOrigin: PRESSURE_GAUGE_ORIGIN
        });
        gsap.set(ftNeedle, {
          rotation: FT_NEEDLE_MIN_DEG + f * FT_NEEDLE_SWEEP_DEG,
          svgOrigin: FLOW_GAUGE_ORIGIN
        });
      } else {
        gsap.to(gateDisc, {
          attr: { y: gateY },
          duration: GAUGE_ANIMATION_DURATION_S,
          ease: "power2.out",
          overwrite: "auto"
        });
        gsap.to(gaugeNeedle, {
          rotation: needleDeg,
          svgOrigin: PRESSURE_GAUGE_ORIGIN,
          duration: GAUGE_ANIMATION_DURATION_S,
          ease: "power2.out",
          overwrite: "auto"
        });
        gsap.to(ftNeedle, {
          rotation: FT_NEEDLE_MIN_DEG + f * FT_NEEDLE_SWEEP_DEG,
          svgOrigin: FLOW_GAUGE_ORIGIN,
          duration: GAUGE_ANIMATION_DURATION_S,
          ease: "power2.out",
          overwrite: "auto"
        });
      }
      this.updateFlowSpeed(pct);
      if (valvePctLabel) {
        valvePctLabel.textContent = pct + "%";
      }
    },

    onPumpToggle() {
      this.playOneShot(this.pumpOn ? this.pumpStartAudio : this.pumpStopAudio);
      if (this.pumpOn) {
        this.applyAnimation(this.valvePosition);
        this.fireReadings();
      } else {
        this.readings = null;
        this.settling = false;
        if (this.settleTimer) clearTimeout(this.settleTimer);
        // Reset the full schematic to its no-flow baseline so all visuals
        // (gate, needles, flow speed, and percentage label) match pump OFF.
        this.applyAnimation(0);
      }
    },

    onHandwheelKey(e) {
      const prevVal = this.valvePosition;
      let newVal = this.valvePosition;
      switch (e.key) {
        case "ArrowRight":
        case "ArrowUp":
          newVal = Math.min(PERCENT_SCALE, newVal + KEYBOARD_STEP_PCT);
          break;
        case "ArrowLeft":
        case "ArrowDown":
          newVal = Math.max(0, newVal - KEYBOARD_STEP_PCT);
          break;
        case "PageUp":
          newVal = Math.min(PERCENT_SCALE, newVal + KEYBOARD_BIG_STEP_PCT);
          break;
        case "PageDown":
          newVal = Math.max(0, newVal - KEYBOARD_BIG_STEP_PCT);
          break;
        case "Home":
          newVal = 0;
          break;
        case "End":
          newVal = 100;
          break;
        default:
          return; // don't preventDefault for unhandled keys
      }
      e.preventDefault();
      this.valvePosition = newVal;
      this.wheelRotation =
        (newVal / PERCENT_SCALE) * TURNS_FOR_FULL_OPEN * DEG_PER_TURN;
      const handwheelSpokes = this.svgRef("handwheelSpokes");
      if (this.animationsPaused) {
        gsap.set(handwheelSpokes, {
          rotation: this.wheelRotation,
          svgOrigin: HANDWHEEL_ORIGIN
        });
      } else {
        gsap.to(handwheelSpokes, {
          rotation: this.wheelRotation,
          svgOrigin: HANDWHEEL_ORIGIN,
          duration: HANDWHEEL_KEYBOARD_TWEEN_S,
          ease: "power2.out",
          overwrite: "auto"
        });
      }
      this.applyAnimation(this.pumpOn ? newVal : 0);
      if (newVal !== prevVal) this.playWheelTurnSound(true);
      this.fireReadings();
    },

    onValveChange() {
      // Positive wheel rotation increases valve opening (clockwise on screen).
      this.wheelRotation =
        (this.valvePosition / PERCENT_SCALE) *
        TURNS_FOR_FULL_OPEN *
        DEG_PER_TURN;
      const handwheelSpokes = this.svgRef("handwheelSpokes");
      if (this.animationsPaused) {
        gsap.set(handwheelSpokes, {
          rotation: this.wheelRotation,
          svgOrigin: HANDWHEEL_ORIGIN
        });
      } else {
        gsap.to(handwheelSpokes, {
          rotation: this.wheelRotation,
          svgOrigin: HANDWHEEL_ORIGIN,
          duration: HANDWHEEL_SLIDER_TWEEN_S,
          ease: "power2.out",
          overwrite: "auto"
        });
      }
      this.playWheelTurnSound(true);
      this.fireReadings();
    },

    startDrag(e) {
      this.dragActive = true;
      this.dragLastAngle = this.pointerAngle(e);
      if (this.wheelKeySoundStopTimer) {
        clearTimeout(this.wheelKeySoundStopTimer);
        this.wheelKeySoundStopTimer = null;
      }

      const svg = this.svgRef("schematicSvg");
      svg.addEventListener("pointermove", this.onDrag);
      svg.addEventListener("pointerup", this.endDrag);
      svg.addEventListener("pointercancel", this.endDrag);
      svg.setPointerCapture(e.pointerId);
    },

    onDrag(e) {
      if (!this.dragActive) return;
      const currentAngle = this.pointerAngle(e);
      let delta = currentAngle - this.dragLastAngle;
      if (delta > 180) delta -= 360;
      if (delta < -180) delta += 360;
      this.dragLastAngle = currentAngle;

      // Only play wheel audio when there is actual rotation, not just a grab.
      if (Math.abs(delta) < 0.01) {
        if (this.wheelAudio && !this.wheelAudio.paused) {
          this.stopWheelDragSound();
        }
        return;
      }
      if (this.wheelAudio && this.wheelAudio.paused) {
        this.startWheelDragSound();
      }

      // Keep wheel rotation as a continuous float — no snapping
      this.wheelRotation = Math.min(
        TURNS_FOR_FULL_OPEN * DEG_PER_TURN,
        Math.max(0, this.wheelRotation + delta)
      );
      gsap.set(this.svgRef("handwheelSpokes"), {
        rotation: this.wheelRotation,
        svgOrigin: HANDWHEEL_ORIGIN
      });

      // Derive exact float pct for smooth animation
      const exactPct =
        (this.wheelRotation / (TURNS_FOR_FULL_OPEN * DEG_PER_TURN)) *
        PERCENT_SCALE;
      const f = exactPct / PERCENT_SCALE;
      // Animate gate disc and gauge instantly during drag (no tween lag)
      gsap.set(this.svgRef("gateDisc"), {
        attr: { y: GATE_CLOSED_Y - f * GATE_TRAVEL }
      });
      gsap.set(this.svgRef("gaugeNeedle"), {
        rotation: NEEDLE_MIN_DEG + f * (NEEDLE_MAX_DEG - NEEDLE_MIN_DEG),
        svgOrigin: PRESSURE_GAUGE_ORIGIN
      });
      gsap.set(this.svgRef("ftNeedle"), {
        rotation: FT_NEEDLE_MIN_DEG + f * FT_NEEDLE_SWEEP_DEG,
        svgOrigin: FLOW_GAUGE_ORIGIN
      });
      this.updateFlowSpeed(exactPct);
      const valvePctLabel = this.svgRef("valvePctLabel");
      if (valvePctLabel) {
        valvePctLabel.textContent = Math.round(exactPct) + "%";
      }

      // Snap to the slider step for display and readings
      this.valvePosition = Math.max(
        0,
        Math.min(
          PERCENT_SCALE,
          Math.round(exactPct / SLIDER_STEP_PCT) * SLIDER_STEP_PCT
        )
      );
      this.fireReadings();
    },

    endDrag(e) {
      if (!this.dragActive) return;
      this.dragActive = false;
      this.stopWheelDragSound();
      const svg = this.svgRef("schematicSvg");
      svg.removeEventListener("pointermove", this.onDrag);
      svg.removeEventListener("pointerup", this.endDrag);
      svg.removeEventListener("pointercancel", this.endDrag);
      if (svg.hasPointerCapture(e.pointerId)) {
        svg.releasePointerCapture(e.pointerId);
      }
    },

    pointerAngle(e) {
      const svg = this.svgRef("schematicSvg");
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const svgPt = pt.matrixTransform(svg.getScreenCTM().inverse());
      // Handwheel center is fixed at (650, -34) in SVG space
      return (
        Math.atan2(svgPt.y - HANDWHEEL_CENTER_Y, svgPt.x - HANDWHEEL_CENTER_X) *
        (180 / Math.PI)
      );
    },

    fireReadings() {
      if (this.settleTimer) clearTimeout(this.settleTimer);
      if (!this.pumpOn || this.valvePosition <= 0) {
        this.readings = null;
        this.settling = false;
        return;
      }
      this.settling = true;
      this.settleTimer = setTimeout(() => {
        this.readings = getTestStandReadings(this.valvePosition);
        this.settling = false;
        this.$emit("readings-ready", this.readings);
      }, pumpTestStand.settlingTime_ms);
    }
  },
  beforeUnmount() {
    if (this.settleTimer) clearTimeout(this.settleTimer);
    if (this.wheelKeySoundStopTimer) {
      clearTimeout(this.wheelKeySoundStopTimer);
      this.wheelKeySoundStopTimer = null;
    }
    this._flowTweens?.forEach((t) => t.kill());
    if (this._impellerTween) this._impellerTween.kill();
    this._cavFlashTween?.kill();
    if (this.wheelAudio) {
      this.stopWheelDragSound();
      this.wheelAudio = null;
    }
    if (this.pumpStartAudio) {
      this.pumpStartAudio.pause();
      this.pumpStartAudio = null;
    }
    if (this.pumpStopAudio) {
      this.pumpStopAudio.pause();
      this.pumpStopAudio = null;
    }
    if (this.overheatAlertAudio) {
      this.stopOverheatAlertSound();
      this.overheatAlertAudio = null;
    }
    // Clean up drag listeners if component unmounts mid-drag
    if (this.dragActive) {
      const svg = this.svgRef("schematicSvg");
      svg.removeEventListener("pointermove", this.onDrag);
      svg.removeEventListener("pointerup", this.endDrag);
      svg.removeEventListener("pointercancel", this.endDrag);
    }
  }
};
</script>

<style scoped>
/* Pump shaking animation when overheating */
.pump-shake {
  animation: shake 0.15s infinite alternate;
  transform-box: fill-box;
  transform-origin: center center;
}

@keyframes shake {
  0% {
    transform: translate(-1.5px, 0.5px) rotate(-0.5deg);
  }
  25% {
    transform: translate(1.5px, -0.5px) rotate(0.5deg);
  }
  50% {
    transform: translate(-0.5px, 1px) rotate(-0.3deg);
  }
  75% {
    transform: translate(1px, -1px) rotate(0.3deg);
  }
  100% {
    transform: translate(0.5px, 0.5px) rotate(0.5deg);
  }
}

/* Sound wave arcs pulsing outward */
.sound-wave {
  opacity: 0;
  animation: pulse-wave 1.2s ease-out infinite;
  transform-box: fill-box;
  transform-origin: left center;
}
.wave-1 {
  animation-delay: 0s;
}
.wave-2 {
  animation-delay: 0.4s;
}
.wave-3 {
  animation-delay: 0.8s;
}

.animations-paused .pump-shake,
.animations-paused .sound-wave {
  animation-play-state: paused;
}

@keyframes pulse-wave {
  0% {
    opacity: 0.9;
    transform: translateX(0);
  }
  100% {
    opacity: 0;
    transform: translateX(8px);
  }
}

/* Keyboard focus ring for SVG handwheel (WCAG 2.4.7) */
circle[role="slider"]:focus {
  outline: 3px solid #4338ca;
  outline-offset: 2px;
}
circle[role="slider"]:focus:not(:focus-visible) {
  outline: none;
}
</style>
